import { useState } from 'react';
import { motion } from 'framer-motion';
import { SEO } from '@/components/SEO';
import { Scale, Users, MessageCircle, Heart, CheckCircle, Clock, ArrowRight, BadgeDollarSign, ExternalLink } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { SERVICES_DATA, ROUTE_PATHS } from '@/lib/index';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { IMAGES } from '@/assets/images';

const iconMap = {
  Scale,
  Users,
  MessageCircle,
  Heart,
};

export default function Services() {
  const [activeTab, setActiveTab] = useState(SERVICES_DATA[0].id);

  return (
    <Layout>
      <SEO
        title="Nos services — Expertise familiale, médiation, coaching coparental"
        description="Découvrez nos services spécialisés : évaluation psychosociale, médiation familiale subventionnée, coaching coparental et rétablissement du lien parent-enfant à Gatineau, Outaouais."
        keywords="services expertise familiale, médiation familiale subventionnée Québec, coaching coparental Gatineau, rétablissement lien parent enfant, évaluation psychosociale garde"
        canonical="/services"
      />
      <div className="min-h-screen bg-background">
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 z-0 opacity-30">
            <img
              src={IMAGES.CONSULTATION_2}
              alt="Services professionnels"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6 text-foreground">
                Nos services spécialisés
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Des interventions professionnelles adaptées à vos besoins familiaux, toujours centrées sur l'intérêt de l'enfant
              </p>
            </motion.div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-1 md:grid-cols-4 h-auto gap-2 bg-background/50 p-2">
                  {SERVICES_DATA.map((service) => {
                    const IconComponent = iconMap[service.icone as keyof typeof iconMap];
                    return (
                      <TabsTrigger
                        key={service.id}
                        value={service.id}
                        className="flex flex-col items-center gap-2 py-4 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                      >
                        {IconComponent && <IconComponent className="w-5 h-5" />}
                        <span className="text-sm font-medium text-center">{service.titre}</span>
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                {SERVICES_DATA.map((service) => {
                  const IconComponent = iconMap[service.icone as keyof typeof iconMap];
                  return (
                    <TabsContent key={service.id} value={service.id} className="mt-8">
                      <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Card className="border-border shadow-lg">
                          <CardHeader className="space-y-4">
                            <div className="flex items-start gap-4">
                              <div className="p-3 rounded-lg bg-primary/10">
                                {IconComponent && <IconComponent className="w-8 h-8 text-primary" />}
                              </div>
                              <div className="flex-1">
                                <CardTitle className="text-3xl mb-2">{service.titre}</CardTitle>
                                <CardDescription className="text-base">
                                  {service.description}
                                </CardDescription>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-8">
                            <div>
                              <h3 className="text-xl font-semibold mb-4 text-foreground">Description complète</h3>
                              <p className="text-muted-foreground leading-relaxed">
                                {service.details.descriptionComplete}
                              </p>
                            </div>

                            <Separator />

                            <div>
                              <h3 className="text-xl font-semibold mb-6 text-foreground">Processus étape par étape</h3>
                              <div className="space-y-6">
                                {service.details.processus.map((etape, index) => (
                                  <motion.div
                                    key={etape.etape}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    className="flex gap-4"
                                  >
                                    <div className="flex-shrink-0">
                                      <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                                        {etape.etape}
                                      </div>
                                    </div>
                                    <div className="flex-1 pb-6 border-l-2 border-border pl-6 ml-5 relative">
                                      {index < service.details.processus.length - 1 && (
                                        <div className="absolute left-0 top-10 bottom-0 w-0.5 bg-border" />
                                      )}
                                      <h4 className="font-semibold text-lg mb-2 text-foreground">{etape.titre}</h4>
                                      <p className="text-muted-foreground">{etape.description}</p>
                                    </div>
                                  </motion.div>
                                ))}
                              </div>
                            </div>

                            <Separator />

                            <div>
                              <h3 className="text-xl font-semibold mb-4 text-foreground">Pour qui?</h3>
                              <ul className="space-y-3">
                                {service.details.pourQui.map((item, index) => (
                                  <motion.li
                                    key={index}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    className="flex items-start gap-3"
                                  >
                                    <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                                    <span className="text-muted-foreground">{item}</span>
                                  </motion.li>
                                ))}
                              </ul>
                            </div>

                            {service.details.duree && (
                              <>
                                <Separator />
                                <div className="flex items-start gap-3">
                                  <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                                  <div>
                                    <h4 className="font-semibold mb-1 text-foreground">Durée</h4>
                                    <p className="text-muted-foreground">{service.details.duree}</p>
                                  </div>
                                </div>
                              </>
                            )}

                            {service.details.subvention && (
                              <>
                                <Separator />
                                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                                  <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                                      <BadgeDollarSign className="w-5 h-5 text-green-700" />
                                    </div>
                                    <div>
                                      <h4 className="font-semibold mb-2 text-green-800">{service.details.subvention.titre}</h4>
                                      <p className="text-green-700 text-sm leading-relaxed mb-3">{service.details.subvention.texte}</p>
                                      <a
                                        href={service.details.subvention.lien}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 text-green-800 font-medium text-sm hover:underline"
                                      >
                                        <ExternalLink className="w-4 h-4" />
                                        {service.details.subvention.lienTexte}
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}

                            <div className="pt-4">
                              <Link to={ROUTE_PATHS.CONTACT}>
                                <Button size="lg" className="w-full md:w-auto">
                                  Demander une consultation
                                  <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                              </Link>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </motion.div>
          </div>
        </section>

        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl font-bold mb-8 text-center text-foreground">
                Comprendre les différences entre nos services
              </h2>
              <Card className="border-border shadow-lg">
                <CardContent className="pt-6">
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <Badge variant="outline" className="text-sm">
                          Expertise vs Médiation
                        </Badge>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        L'<strong className="text-foreground">expertise psychosociale</strong> est une évaluation professionnelle mandatée par le tribunal pour formuler des recommandations objectives. La <strong className="text-foreground">médiation familiale</strong> est un processus volontaire où les parents travaillent ensemble pour trouver des solutions mutuellement acceptables.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <Badge variant="outline" className="text-sm">
                          Médiation vs Coaching
                        </Badge>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        La <strong className="text-foreground">médiation</strong> vise à résoudre des différends spécifiques et établir des ententes formelles. Le <strong className="text-foreground">coaching coparental</strong> se concentre sur le développement de compétences de communication et de collaboration à long terme.
                      </p>
                    </div>

                    <Separator />

                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <Badge variant="outline" className="text-sm">
                          Coaching vs Rétablissement du lien
                        </Badge>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">
                        Le <strong className="text-foreground">coaching coparental</strong> améliore la communication entre parents fonctionnels. Le <strong className="text-foreground">rétablissement du lien</strong> intervient dans des situations de rupture relationnelle parent-enfant nécessitant une reconstruction progressive.
                      </p>
                    </div>

                    <div className="pt-4 bg-muted/50 rounded-lg p-6 mt-6">
                      <p className="text-sm text-muted-foreground text-center">
                        <strong className="text-foreground">Besoin d'aide pour choisir?</strong> Contactez-nous pour une consultation initiale. Nous vous aiderons à identifier le service le plus adapté à votre situation.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        <section className="py-16 bg-primary/5">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl font-bold mb-6 text-foreground">
                Prêt à faire le premier pas?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Nous sommes là pour vous accompagner avec professionnalisme, neutralité et bienveillance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to={ROUTE_PATHS.CONTACT}>
                  <Button size="lg" className="w-full sm:w-auto">
                    Prendre rendez-vous
                  </Button>
                </Link>
                <Link to={ROUTE_PATHS.FAQ}>
                  <Button size="lg" variant="outline" className="w-full sm:w-auto">
                    Consulter la FAQ
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
